<template>
  <div class="whatsapp-chat-widget">
    <div class="whatsapp-chat-widget__actions">
      <button
        type="button"
        class="whatsapp-chat-widget__teaser"
        aria-label="Open booking assistant"
        @click="openModalFromEvent"
      >
        <span
          class="whatsapp-chat-widget__message"
          :class="{ 'is-blinking': !isOpen }"
        >
          <span class="whatsapp-chat-widget__message-line">{{ teaserLines[0] }}</span>
          <span v-if="teaserLines[1]" class="whatsapp-chat-widget__message-line">{{ teaserLines[1] }}</span>
        </span>
        <span class="whatsapp-chat-widget__icon whatsapp-chat-widget__icon--bot">
          <span class="whatsapp-chat-widget__bubbles" aria-hidden="true">
            <i class="fa fa-comment whatsapp-chat-widget__bubble-back"></i>
            <i class="fa fa-comment whatsapp-chat-widget__bubble-front"></i>
          </span>
        </span>
      </button>

      <button
        type="button"
        class="whatsapp-chat-widget__whatsapp-button"
        aria-label="Open WhatsApp"
        @click="openWhatsAppLink"
      >
        <i class="fa fa-whatsapp" aria-hidden="true"></i>
      </button>
    </div>

    <div
      v-if="isOpen"
      class="whatsapp-chat-widget__backdrop"
      role="presentation"
    ></div>

    <section
      v-if="isOpen"
      class="whatsapp-chat-widget__modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="whatsapp-chat-title"
      @click.stop
    >
      <header class="whatsapp-chat-widget__header">
        <div>
          <span class="whatsapp-chat-widget__status"></span>
          <h2 id="whatsapp-chat-title">Dubai Pets Groomer</h2>
          <p>Booking assistant</p>
        </div>
        <button
          type="button"
          class="whatsapp-chat-widget__close"
          aria-label="Close booking assistant"
          @click="closeModal"
        >
          &times;
        </button>
      </header>

      <div ref="chatBodyScroll" class="whatsapp-chat-widget__body">
        <div ref="chatBody" class="whatsapp-chat-widget__thread">
          <div
            v-for="(chatMessage, index) in messages"
            :key="index"
            class="whatsapp-chat-widget__row"
            :class="chatMessage.sender === 'bot' ? 'is-bot' : 'is-user'"
          >
            <div class="whatsapp-chat-widget__bubble">
              {{ chatMessage.text }}
            </div>
          </div>

          <div v-if="isSubmitting" class="whatsapp-chat-widget__row is-bot">
            <div class="whatsapp-chat-widget__bubble">Submitting your booking request...</div>
          </div>
        </div>
      </div>

      <div v-if="quickOptions.length" class="whatsapp-chat-widget__quick-replies">
        <button
          v-for="option in quickOptions"
          :key="option.value"
          type="button"
          :disabled="isSubmitting"
          @click="selectOption(option)"
        >
          {{ option.label }}
        </button>
      </div>

      <footer
        v-if="botState === 'awaiting_address'"
        class="whatsapp-chat-widget__footer whatsapp-chat-widget__footer--location"
      >
        <button
          type="button"
          class="whatsapp-chat-widget__pick-location"
          :disabled="isSubmitting"
          @click="openLocationPicker"
        >
          Pick Location
        </button>
      </footer>

      <footer
        v-else-if="botState === 'completed' && bookingReceiptId"
        class="whatsapp-chat-widget__footer whatsapp-chat-widget__footer--location"
      >
        <button
          type="button"
          class="whatsapp-chat-widget__download-receipt"
          :disabled="isDownloadingReceipt"
          @click="downloadBookingPDF"
        >
          {{ isDownloadingReceipt ? 'Downloading...' : 'Download Receipt' }}
        </button>
      </footer>

      <footer
        v-else-if="botState === 'awaiting_phone'"
        class="whatsapp-chat-widget__footer whatsapp-chat-widget__footer--phone"
      >
        <div class="whatsapp-chat-widget__phone-input">
          <span class="whatsapp-chat-widget__phone-prefix">
            <img
              src="https://cdn.jsdelivr.net/npm/country-flag-icons/3x2/AE.svg"
              alt="UAE"
              class="whatsapp-chat-widget__phone-flag"
            />
            +971
          </span>
          <input
            v-model="phoneLocalNumber"
            type="tel"
            class="whatsapp-chat-widget__input whatsapp-chat-widget__input--phone"
            placeholder="501234567"
            :disabled="isSubmitting"
            maxlength="9"
            @keyup.enter="submitPhoneNumber"
          />
        </div>
        <button
          type="button"
          class="whatsapp-chat-widget__send"
          :disabled="isSubmitting"
          @click="submitPhoneNumber"
        >
          Send
        </button>
      </footer>

      <footer
        v-else-if="botState === 'awaiting_date'"
        class="whatsapp-chat-widget__footer whatsapp-chat-widget__footer--location"
      >
        <button
          type="button"
          class="whatsapp-chat-widget__pick-location"
          :disabled="isSubmitting"
          @click="openDatePicker"
        >
          Pick Date
        </button>
      </footer>

      <footer v-else class="whatsapp-chat-widget__footer">
        <input
          v-model="userInput"
          type="text"
          class="whatsapp-chat-widget__input"
          :placeholder="inputPlaceholder"
          :disabled="isSubmitting || botState === 'completed'"
          @keyup.enter="submitInput"
        />
        <button
          type="button"
          class="whatsapp-chat-widget__send"
          :disabled="isSubmitting || botState === 'completed'"
          @click="submitInput"
        >
          Send
        </button>
      </footer>
    </section>

    <div
      class="modal fade"
      id="BotLocationPickerModal"
      data-bs-backdrop="static"
      data-bs-keyboard="false"
      tabindex="-1"
      aria-labelledby="botLocationPickerLabel"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-lg">
        <div class="modal-content bg-introduction rounded-3 padding-bottom-2">
          <div class="modal-header pb-0 border-bottom-0 padding-top-3 w-100 d-inline-block padding-left-3 padding-right-3">
            <h1 class="text-black mb-0 medium-family float-start text-size-14 text-full-14">Select Location</h1>
            <button
              type="button"
              class="btn-close mt-0 rounded-circle p-0 d-inline-block close-btn float-end m-0"
              @click="closeLocationModal"
              aria-label="Close"
            ></button>
          </div>
          <div class="modal-body padding-2 position-relative">
            <div class="w-100 d-inline-block margin-top-2 position-relative">
              <div class="row">
                <div class="col-xl-10 col-12">
                  <form class="position-relative booking-banner" @submit.prevent>
                    <input
                      type="text"
                      v-model="searchQuery"
                      @input="handleLocationInput"
                      placeholder="Search for area, street name, landmark..."
                      class="bg-white box-shadow rounded-3 border-0 text-size-16 text-full-13 w-100"
                    />
                    <a class="position-absolute location-icon" href="javascript:void(0)">
                      <img src="@/assets/images/location-service-icon.svg" alt="Location icon">
                    </a>
                    <i class="fa fa-angle-down position-absolute nav-color angle-right-icon" aria-hidden="true"></i>
                  </form>
                </div>
                <div class="col-xl-2 col-12 mt-xl-0 mt-3 ps-xl-0">
                  <div class="text-end margin-top-2">
                    <button
                      type="button"
                      class="btn btn-success px-4 py-2 text-white text-size-14 rounded-3 text-full-14"
                      @click="confirmSearchedLocation"
                    >
                      OK
                    </button>
                  </div>
                </div>
              </div>

              <div v-if="predictions.length" class="predictions-container position-absolute w-100 margin-top-1 z-3">
                <div
                  v-for="(suggestion, index) in predictions"
                  :key="index"
                  class="prediction-item padding-2 text-start bg-white border-bottom cursor-pointer text-full-13 text-size-14"
                  @click="selectLocationSuggestion(suggestion)"
                >
                  {{ suggestion.description }}
                </div>
              </div>
            </div>

            <div class="w-100 d-inline-block map-scroll">
              <div class="w-100 d-inline-block margin-top-3 rounded-3 confirm-location-map">
                <div id="bot-map" style="width: 100%; height: 100%;"></div>
                <div
                  v-if="!locationConfirmed"
                  class="position-absolute start-50 translate-middle text-center text-white bg-dark bg-opacity-75 rounded-2"
                  style="width: 310px; top: 75%; padding: 0 1rem !important;"
                >
                  Move the map to set the exact position
                </div>
              </div>
            </div>

            <div v-if="locationConfirmed" class="w-100 d-inline-block confirm-location-btn">
              <button
                type="button"
                class="btn bg-green px-4 py-2 text-white text-size-14 rounded-3 text-full-14 w-100 d-inline-block"
                @click="confirmPinLocation"
              >
                Confirm Pin Location
              </button>
            </div>
            <div v-else class="w-100 d-inline-block confirm-location-btn">
              <button
                type="button"
                class="btn bg-green px-4 py-2 text-white text-size-14 rounded-3 text-full-14 w-100 d-inline-block disabled"
                disabled
              >
                Confirm Pin Location
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      class="modal fade"
      id="BotDatePickerModal"
      data-bs-backdrop="static"
      data-bs-keyboard="false"
      tabindex="-1"
      aria-labelledby="botDatePickerLabel"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content bg-introduction rounded-3 padding-bottom-2">
          <div class="modal-header pb-0 border-bottom-0 padding-top-3 w-100 d-inline-block padding-left-3 padding-right-3">
            <h1 id="botDatePickerLabel" class="text-black mb-0 medium-family float-start text-size-14 text-full-14">Select Date</h1>
            <button
              type="button"
              class="btn-close mt-0 rounded-circle p-0 d-inline-block close-btn float-end m-0"
              @click="closeDatePickerModal"
              aria-label="Close"
            ></button>
          </div>
          <div class="modal-body padding-2">
            <div class="whatsapp-chat-widget__calendar-nav">
              <button
                type="button"
                class="whatsapp-chat-widget__calendar-nav-btn"
                :disabled="!canGoPrevMonth"
                @click="prevCalendarMonth"
              >
                ‹
              </button>
              <span class="whatsapp-chat-widget__calendar-month">{{ calendarMonthLabel }}</span>
              <button
                type="button"
                class="whatsapp-chat-widget__calendar-nav-btn"
                @click="nextCalendarMonth"
              >
                ›
              </button>
            </div>

            <div class="whatsapp-chat-widget__calendar-weekdays">
              <span v-for="weekday in weekdayLabels" :key="weekday">{{ weekday }}</span>
            </div>

            <div class="whatsapp-chat-widget__calendar-grid">
              <template v-for="(week, weekIndex) in calendarWeeks" :key="weekIndex">
                <button
                  v-for="(day, dayIndex) in week"
                  :key="`${weekIndex}-${dayIndex}`"
                  type="button"
                  class="whatsapp-chat-widget__calendar-day"
                  :class="{
                    'is-empty': !day,
                    'is-disabled': day && day.disabled,
                    'is-selected': day && day.selected,
                    'is-today': day && day.isToday,
                  }"
                  :disabled="!day || day.disabled"
                  @click="day && selectCalendarDate(day.iso)"
                >
                  {{ day ? day.day : '' }}
                </button>
              </template>
            </div>

            <button
              type="button"
              class="btn bg-green px-4 py-2 text-white text-size-14 rounded-3 text-full-14 w-100 d-inline-block margin-top-3"
              :disabled="!modalSelectedDate"
              @click="confirmSelectedDate"
            >
              Confirm Date
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* global google */
import { getBookingDownloadUrl } from '@/utils/utils';

export default {
  name: 'WhatsAppChatModal',
  data() {
    return {
      isOpen: false,
      isSubmitting: false,
      userInput: '',
      selectedDate: '',
      modalSelectedDate: '',
      calendarViewDate: new Date(),
      phoneLocalNumber: '',
      dubaiDialCode: '+971',
      botState: 'new',
      botPayload: {},
      messages: [],
      services: [],
      timeSlots: [],
      quickOptions: [],
      bookingReceiptId: null,
      isDownloadingReceipt: false,
      searchQuery: '',
      predictions: [],
      address: '',
      lat: null,
      lng: null,
      map: null,
      marker: null,
      locationConfirmed: false,
      autoOpenTimer: null,
      userClosedChat: false,
    };
  },
  computed: {
    currentContent() {
      const path = this.$route.path.toLowerCase();
      const defaultContent = {
        greeting: 'Hello! I can help you book pet grooming service.',
        categoryHint: null,
      };

      const pageMessages = [
        { match: 'mobile-dog-grooming', content: { greeting: 'Hello! I can help you book grooming for your dog.', categoryHint: 'Dog Grooming' } },
        { match: 'mobile-cat-grooming', content: { greeting: 'Hello! I can help you book grooming for your cat.', categoryHint: 'Cat Grooming' } },
        { match: 'bird-grooming', content: { greeting: 'Hello! I can help you book grooming for your bird.', categoryHint: 'Birds Grooming' } },
        { match: 'rabbit-grooming', content: { greeting: 'Hello! I can help you book grooming for your rabbit.', categoryHint: 'Rabbit Grooming' } },
        { match: 'booking', content: { greeting: 'Hello! I can help you complete your grooming booking.', categoryHint: null } },
        { match: 'contact-us', content: { greeting: 'Hello! I can help with grooming service information and booking.', categoryHint: null } },
      ];

      const pageMessage = pageMessages.find(({ match }) => path.includes(match));
      return pageMessage ? pageMessage.content : defaultContent;
    },
    inputPlaceholder() {
      if (this.botState === 'completed') return 'Booking completed';
      if (this.quickOptions.length) return 'Or type your answer...';
      return 'Type your message...';
    },
    todayDate() {
      return this.formatLocalDate(new Date());
    },
    weekdayLabels() {
      return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    },
    calendarMonthLabel() {
      return this.calendarViewDate.toLocaleString('default', { month: 'long', year: 'numeric' });
    },
    canGoPrevMonth() {
      const today = new Date();
      const viewMonthStart = new Date(this.calendarViewDate.getFullYear(), this.calendarViewDate.getMonth(), 1);
      const currentMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
      return viewMonthStart > currentMonthStart;
    },
    calendarWeeks() {
      const year = this.calendarViewDate.getFullYear();
      const month = this.calendarViewDate.getMonth();
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const weeks = [];
      let week = [];

      for (let i = 0; i < firstDay.getDay(); i += 1) {
        week.push(null);
      }

      for (let day = 1; day <= lastDay.getDate(); day += 1) {
        const date = new Date(year, month, day);
        const iso = this.formatLocalDate(date);

        week.push({
          day,
          iso,
          disabled: date < today,
          selected: this.modalSelectedDate === iso,
          isToday: date.getTime() === today.getTime(),
        });

        if (week.length === 7) {
          weeks.push(week);
          week = [];
        }
      }

      if (week.length) {
        while (week.length < 7) {
          week.push(null);
        }
        weeks.push(week);
      }

      return weeks;
    },
    categories() {
      return this.services.map((group) => group.category_name).filter(Boolean);
    },
    petTypeOptions() {
      const options = [
        { label: 'Dog', value: 'dog', categoryName: 'Dog Grooming' },
        { label: 'Cat', value: 'cat', categoryName: 'Cat Grooming' },
        { label: 'Rabbit', value: 'rabbit', categoryName: 'Rabbit Grooming' },
        { label: 'Bird', value: 'bird', categoryName: 'Birds Grooming' },
      ];

      return options.filter((option) => this.categories.includes(option.categoryName));
    },
    servicesForSelectedCategory() {
      const group = this.services.find((item) => item.category_name === this.botPayload.category_name);
      return group ? group.services : [];
    },
    teaserLines() {
      return ['Need Help!', 'Chat with us'];
    },
  },
  mounted() {
    window.addEventListener('open-whatsapp-chat', this.openModalFromEvent);
    this.$router.isReady().then(() => {
      this.onPageReadyForAutoOpen();
    });
  },
  beforeUnmount() {
    window.removeEventListener('open-whatsapp-chat', this.openModalFromEvent);
    this.clearAutoOpenTimer();
  },
  methods: {
    openWhatsAppLink() {
      const phoneNumber = '+971522809076';
      const message = 'Hello, I am Looking for Grooming Services';
      const encodedMessage = encodeURIComponent(message);
      const whatsappUrl = `https://api.whatsapp.com/send/?phone=${phoneNumber}&text=${encodedMessage}&type=phone_number&app_absent=0`;

      window.open(whatsappUrl, '_blank');
    },
    openModalFromEvent() {
      this.openModal();
    },
    hasAutoOpenedThisSession() {
      try {
        return sessionStorage.getItem('whatsapp-bot-auto-shown') === 'true';
      } catch (error) {
        return false;
      }
    },
    markAutoOpenedThisSession() {
      try {
        sessionStorage.setItem('whatsapp-bot-auto-shown', 'true');
      } catch (error) {
        // Ignore storage access errors (private mode, blocked storage, etc.).
      }
    },
    onPageReadyForAutoOpen() {
      if (this.isOpen || this.userClosedChat || this.hasAutoOpenedThisSession()) return;

      this.startAutoOpenCountdown();
    },
    startAutoOpenCountdown() {
      this.clearAutoOpenTimer();

      if (this.isOpen || this.userClosedChat || this.hasAutoOpenedThisSession()) return;

      this.autoOpenTimer = window.setTimeout(() => {
        this.autoOpenTimer = null;

        if (this.isOpen || this.userClosedChat || this.hasAutoOpenedThisSession()) return;

        this.markAutoOpenedThisSession();
        this.openModal();
      }, 5000);
    },
    async openModal() {
      this.clearAutoOpenTimer();
      this.isOpen = true;
      this.resetBot();
      await this.loadServices();
      this.addBotMessage(this.currentContent.greeting);

      if (this.currentContent.categoryHint && this.categories.includes(this.currentContent.categoryHint)) {
        this.botPayload.category_name = this.currentContent.categoryHint;
        this.botState = 'awaiting_service';
        this.addBotMessage(`Great. Which ${this.currentContent.categoryHint} service do you need?`);
        this.setServiceOptions();
        return;
      }

      this.askPetType();
    },
    closeModal() {
      this.isOpen = false;
      this.userClosedChat = true;
      this.markAutoOpenedThisSession();
      this.clearAutoOpenTimer();
    },
    resetBot() {
      this.userInput = '';
      this.selectedDate = '';
      this.modalSelectedDate = '';
      this.calendarViewDate = new Date();
      this.phoneLocalNumber = '';
      this.botState = 'new';
      this.botPayload = {};
      this.messages = [];
      this.timeSlots = [];
      this.quickOptions = [];
      this.bookingReceiptId = null;
      this.isDownloadingReceipt = false;
      this.isSubmitting = false;
      this.resetLocationPicker();
    },
    resetLocationPicker() {
      this.searchQuery = '';
      this.predictions = [];
      this.address = '';
      this.lat = null;
      this.lng = null;
      this.map = null;
      this.marker = null;
      this.locationConfirmed = false;
    },
    async loadServices() {
      try {
        const response = await this.$axios.post('/booking-services', {
          website_name: this.$websiteName,
        });
        if (response.data.success) {
          this.services = response.data.services || [];
        }
      } catch (error) {
        console.error('Error loading services:', error);
      }
    },
    addBotMessage(text) {
      this.messages.push({ sender: 'bot', text });
      this.$nextTick(this.scrollToBottom);
    },
    addUserMessage(text) {
      this.messages.push({ sender: 'user', text });
      this.$nextTick(this.scrollToBottom);
    },
    scrollToBottom() {
      const body = this.$refs.chatBodyScroll;
      if (body) body.scrollTop = body.scrollHeight;
    },
    setQuickOptions(options) {
      this.quickOptions = options;
    },
    selectOption(option) {
      this.userInput = '';
      this.addUserMessage(option.label);
      this.quickOptions = [];
      this.handleAnswer(option.value);
    },
    submitInput() {
      const value = this.userInput.trim();
      if (!value || this.isSubmitting || this.botState === 'completed') return;

      this.addUserMessage(value);
      this.userInput = '';
      this.quickOptions = [];
      this.handleAnswer(value);
    },
    submitSelectedDate() {
      if (!this.selectedDate || this.isSubmitting || this.botState !== 'awaiting_date') return;

      this.addUserMessage(this.selectedDate);
      this.handleDate(this.selectedDate);
    },
    submitPhoneNumber() {
      if (this.isSubmitting || this.botState !== 'awaiting_phone') return;

      const localNumber = this.phoneLocalNumber.replace(/\D/g, '').replace(/^0+/, '');
      if (!localNumber) return;

      this.addUserMessage(`${this.dubaiDialCode} ${localNumber}`);
      this.phoneLocalNumber = '';
      this.handlePhone(`${this.dubaiDialCode}${localNumber}`);
    },
    askPetType() {
      if (!this.petTypeOptions.length) {
        this.addBotMessage('Sorry, services are not available right now. Please try again later.');
        this.botState = 'completed';
        return;
      }

      this.botState = 'awaiting_pet_type';
      this.addBotMessage(`Which pet needs grooming?\n${this.petTypeOptions.map((option, index) => `${index + 1}. ${option.label}`).join('\n')}`);
      this.setQuickOptions(this.petTypeOptions.map((option, index) => ({
        label: `${index + 1}. ${option.label}`,
        value: option.value,
      })));
    },
    setServiceOptions() {
      const services = this.servicesForSelectedCategory;
      if (!services.length) {
        this.addBotMessage('No services found for this pet type. Please choose another pet.');
        this.askPetType();
        return;
      }

      const lines = services.map((service, index) => {
        return `${index + 1}. ${this.formatServiceOption(service)}`;
      }).join('\n');

      this.addBotMessage(lines);
      this.setQuickOptions(services.map((service, index) => ({
        label: `${index + 1}. ${this.formatServiceOption(service)}`,
        value: String(index + 1),
      })));
    },
    getServicePlanName(service) {
      return service.plan_name
        || service.planName
        || service.plan_detail_name
        || service.plan_detail
        || service.plan
        || '';
    },
    formatServiceOption(service) {
      const planName = this.getServicePlanName(service);
      const plan = planName ? ` - ${planName} Grooming` : '';
      const price = service.price ? ` (AED ${service.price})` : '';
      return `${service.name}${plan}${price}`;
    },
    formatServiceSummary(service) {
      const planName = this.getServicePlanName(service);
      return planName ? `${service.name} - ${planName} Grooming` : service.name;
    },
    async loadTimeSlots(date) {
      try {
        const response = await this.$axios.post('/website-timing', {
          appointment_date: date,
        });

        if (response.data.success) {
          this.timeSlots = (response.data.available_slots || []).map((slot) => ({
            time: slot,
            label: slot,
          }));
        } else {
          this.timeSlots = [];
        }
      } catch (error) {
        console.error('Error loading slots:', error);
        this.timeSlots = [];
      }
    },
    handleAnswer(value) {
      switch (this.botState) {
        case 'awaiting_pet_type':
          this.handlePetType(value);
          break;
        case 'awaiting_service':
          this.handleService(value);
          break;
        case 'awaiting_date':
          this.handleDate(value);
          break;
        case 'awaiting_time':
          this.handleTime(value);
          break;
        case 'awaiting_name':
          this.handleName(value);
          break;
        case 'awaiting_phone':
          this.handlePhone(value);
          break;
        case 'awaiting_address_details':
          this.handleAddressDetails(value);
          break;
        case 'awaiting_confirmation':
          this.handleConfirmation(value);
          break;
        default:
          if (['start', 'restart', 'menu', 'book', 'booking'].includes(value.toLowerCase())) {
            this.resetBot();
            this.addBotMessage(this.currentContent.greeting);
            this.askPetType();
          } else {
            this.addBotMessage('Reply BOOK to start a new booking request.');
          }
      }
    },
    handlePetType(value) {
      const normalizedValue = value.toLowerCase().trim();
      const index = Number(value) - 1;
      const option = this.petTypeOptions[index]
        || this.petTypeOptions.find((item) => (
          item.value === normalizedValue
          || item.label.toLowerCase() === normalizedValue
        ));

      if (!option) {
        this.addBotMessage('Please choose a valid pet type: Dog, Cat, Rabbit, or Bird.');
        this.askPetType();
        return;
      }

      this.botPayload.category_name = option.categoryName;
      this.botState = 'awaiting_service';
      this.addBotMessage(`Which ${option.categoryName} service do you need?`);
      this.setServiceOptions();
    },
    handleService(value) {
      const services = this.servicesForSelectedCategory;
      const index = Number(value) - 1;
      const normalizedValue = value.toLowerCase();
      const service = services[index] || services.find((item) => (
        this.formatServiceOption(item).toLowerCase().includes(normalizedValue)
      ));

      if (!service) {
        this.addBotMessage('Please choose a valid service.');
        this.setServiceOptions();
        return;
      }

      this.botPayload.service = service;
      this.botState = 'awaiting_date';
      this.initializeDatePickerDefaults();
      this.addBotMessage('Please pick your booking date using the date picker below.');
    },
    async handleDate(value) {
      const date = this.parseDate(value);

      if (!date) {
        this.addBotMessage('Please pick a valid date from the calendar.');
        return;
      }

      if (date < this.todayDate) {
        this.addBotMessage('Please select today or a future date.');
        return;
      }

      this.botPayload.date = date;
      await this.loadTimeSlots(date);

      if (!this.timeSlots.length) {
        this.initializeDatePickerDefaults();
        this.addBotMessage(`No slots are available on ${date}. Please select another date.`);
        return;
      }

      this.botState = 'awaiting_time';
      this.addBotMessage(`Available slots for ${date}:\n${this.timeSlots.map((slot, index) => `${index + 1}. ${slot.label}`).join('\n')}`);
      this.setQuickOptions(this.timeSlots.map((slot, index) => ({
        label: `${index + 1}. ${slot.label}`,
        value: String(index + 1),
      })));
    },
    handleTime(value) {
      const index = Number(value) - 1;
      const slot = this.timeSlots[index] || this.timeSlots.find((item) => item.time.includes(value));

      if (!slot) {
        this.addBotMessage('Please choose a valid time slot.');
        this.setQuickOptions(this.timeSlots.map((item, slotIndex) => ({
          label: `${slotIndex + 1}. ${item.label}`,
          value: String(slotIndex + 1),
        })));
        return;
      }

      const [durationFrom, durationTo] = slot.time.split('-');
      this.botPayload.duration_from = durationFrom;
      this.botPayload.duration_to = durationTo;
      this.botState = 'awaiting_name';
      this.addBotMessage('Please share your full name.');
    },
    handleName(value) {
      if (value.length < 2) {
        this.addBotMessage('Please enter your full name.');
        return;
      }

      this.botPayload.name = value;
      this.botState = 'awaiting_phone';
      this.phoneLocalNumber = '';
      this.addBotMessage('Please share your Dubai mobile number.');
    },
    handlePhone(value) {
      let digits = value.replace(/[^\d]/g, '');

      if (digits.startsWith('971')) {
        digits = digits.slice(3);
      }

      digits = digits.replace(/^0+/, '');

      if (digits.length !== 9) {
        this.addBotMessage('Please enter a valid Dubai mobile number (9 digits).');
        return;
      }

      this.botPayload.phoneNumber = `${this.dubaiDialCode}${digits}`;
      this.botState = 'awaiting_address';
      this.addBotMessage('Please pick your grooming location using the location picker below.');
    },
    applySelectedLocation(selectedAddress) {
      if (!selectedAddress || selectedAddress.length < 5) {
        if (this.$toast) this.$toast.error('Please select a valid location.');
        return;
      }

      this.botPayload.base_address = selectedAddress;
      this.botPayload.pin_location = this.buildPinLocationUrl(selectedAddress);
      this.addUserMessage(selectedAddress);
      this.botState = 'awaiting_address_details';
      this.addBotMessage('Please share your apartment/building name, apartment number, or full address details.');
    },
    buildPinLocationUrl(address) {
      if (this.lat && this.lng) {
        return `https://www.google.com/maps?q=${this.lat},${this.lng}`;
      }

      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
    },
    buildFullAddress() {
      const parts = [this.botPayload.base_address];

      if (this.botPayload.address_details) {
        parts.push(this.botPayload.address_details);
      }

      return parts.filter(Boolean).join(', ');
    },
    handleAddressDetails(value) {
      const addressDetails = value.trim();

      if (addressDetails.length < 2) {
        this.addBotMessage('Please share your apartment/building details or full address.');
        return;
      }

      this.botPayload.address_details = addressDetails;
      this.botPayload.address = this.buildFullAddress();
      this.showBookingConfirmation();
    },
    showBookingConfirmation() {
      this.botState = 'awaiting_confirmation';

      const service = this.botPayload.service;
      this.addBotMessage(
        `Please confirm your booking:\n\n`
        + `Pet: ${this.botPayload.category_name}\n`
        + `Service: ${this.formatServiceSummary(service)}\n`
        + `Date: ${this.botPayload.date}\n`
        + `Time: ${this.botPayload.duration_from}-${this.botPayload.duration_to}\n`
        + `Name: ${this.botPayload.name}\n`
        + `Phone: ${this.botPayload.phoneNumber}\n`
        + `Address: ${this.botPayload.address}\n`
        + `Price: AED ${service.price || 0}\n\n`
        + 'Reply YES to submit or NO to start again.'
      );
      this.setQuickOptions([
        { label: 'YES', value: 'yes' },
        { label: 'NO', value: 'no' },
      ]);
    },
    formatLocalDate(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    initializeDatePickerDefaults() {
      this.selectedDate = this.todayDate;
      this.modalSelectedDate = this.todayDate;
      this.calendarViewDate = new Date();
    },
    clearAutoOpenTimer() {
      if (!this.autoOpenTimer) return;

      clearTimeout(this.autoOpenTimer);
      this.autoOpenTimer = null;
    },
    openDatePicker() {
      this.modalSelectedDate = this.selectedDate || this.todayDate;
      this.calendarViewDate = new Date();

      if (this.modalSelectedDate) {
        const [year, month, day] = this.modalSelectedDate.split('-').map(Number);
        this.calendarViewDate = new Date(year, month - 1, day);
      }

      this.$nextTick(() => {
        this.openDatePickerModal();
      });
    },
    openDatePickerModal() {
      const modalElement = document.getElementById('BotDatePickerModal');
      if (!modalElement) return;

      modalElement.classList.add('show');
      modalElement.style.display = 'block';
      document.body.classList.add('modal-open');

      if (!document.querySelector('.modal-backdrop')) {
        const backdrop = document.createElement('div');
        backdrop.className = 'modal-backdrop fade show';
        document.body.appendChild(backdrop);
      }
    },
    closeDatePickerModal() {
      const modalElement = document.getElementById('BotDatePickerModal');
      if (!modalElement) return;

      modalElement.classList.remove('show');
      modalElement.style.display = 'none';
      document.body.classList.remove('modal-open');

      const backdrop = document.querySelector('.modal-backdrop');
      if (backdrop) {
        backdrop.parentNode.removeChild(backdrop);
      }
    },
    prevCalendarMonth() {
      if (!this.canGoPrevMonth) return;
      this.calendarViewDate = new Date(
        this.calendarViewDate.getFullYear(),
        this.calendarViewDate.getMonth() - 1,
        1
      );
    },
    nextCalendarMonth() {
      this.calendarViewDate = new Date(
        this.calendarViewDate.getFullYear(),
        this.calendarViewDate.getMonth() + 1,
        1
      );
    },
    selectCalendarDate(isoDate) {
      if (!isoDate || isoDate < this.todayDate) return;
      this.modalSelectedDate = isoDate;
    },
    confirmSelectedDate() {
      if (!this.modalSelectedDate || this.modalSelectedDate < this.todayDate) {
        if (this.$toast) this.$toast.error('Please select today or a future date.');
        return;
      }

      this.selectedDate = this.modalSelectedDate;
      this.closeDatePickerModal();
      this.submitSelectedDate();
    },
    openLocationPicker() {
      this.resetLocationPicker();
      this.$nextTick(() => {
        this.initBotMap();
      });
    },
    initBotMap() {
      this.openLocationModal();
      this.$nextTick(() => {
        if (!window.google || !document.getElementById('bot-map')) return;

        const defaultLatLng = { lat: 25.2048, lng: 55.2708 };
        this.map = new google.maps.Map(document.getElementById('bot-map'), {
          center: defaultLatLng,
          zoom: 12,
        });
        this.marker = new google.maps.Marker({
          position: defaultLatLng,
          map: this.map,
          draggable: true,
        });

        this.marker.addListener('dragend', (event) => {
          this.locationConfirmed = true;
          this.lat = event.latLng.lat();
          this.lng = event.latLng.lng();
          this.reverseGeocodePin(this.lat, this.lng);
        });

        this.map.addListener('click', (event) => {
          this.marker.setPosition(event.latLng);
          this.lat = event.latLng.lat();
          this.lng = event.latLng.lng();
          this.reverseGeocodePin(this.lat, this.lng);
          this.locationConfirmed = true;
        });

        this.map.addListener('dragend', () => {
          const center = this.map.getCenter();
          this.marker.setPosition(center);
          this.lat = center.lat();
          this.lng = center.lng();
          this.locationConfirmed = true;
          this.reverseGeocodePin(this.lat, this.lng);
        });

        google.maps.event.trigger(this.map, 'resize');
        this.map.setCenter(defaultLatLng);
      });
    },
    async reverseGeocodePin(lat, lng) {
      const geocoder = new google.maps.Geocoder();
      const latLng = { lat: parseFloat(lat), lng: parseFloat(lng) };

      geocoder.geocode({ location: latLng }, (results, status) => {
        if (status === 'OK' && results[0]) {
          this.address = results[0].formatted_address;
          this.searchQuery = this.address;
        }
      });
    },
    openLocationModal() {
      const modalElement = document.getElementById('BotLocationPickerModal');
      if (!modalElement) return;

      modalElement.classList.add('show');
      modalElement.style.display = 'block';
      document.body.classList.add('modal-open');

      if (!document.querySelector('.modal-backdrop')) {
        const backdrop = document.createElement('div');
        backdrop.className = 'modal-backdrop fade show';
        document.body.appendChild(backdrop);
      }
    },
    closeLocationModal() {
      const modalElement = document.getElementById('BotLocationPickerModal');
      if (!modalElement) return;

      modalElement.classList.remove('show');
      modalElement.style.display = 'none';
      document.body.classList.remove('modal-open');

      const backdrop = document.querySelector('.modal-backdrop');
      if (backdrop) {
        backdrop.parentNode.removeChild(backdrop);
      }
    },
    async handleLocationInput() {
      if (this.searchQuery.trim().length > 1) {
        const result = await this.searchPlaces(this.searchQuery);
        if (result && result.predictions) {
          this.predictions = result.predictions;
        }
      } else {
        this.predictions = [];
      }
    },
    async searchPlaces(query) {
      try {
        const response = await this.$axios.post('/search-places', { query });
        return response.data;
      } catch (error) {
        console.error('Error fetching places:', error);
        return null;
      }
    },
    selectLocationSuggestion(suggestion) {
      this.searchQuery = suggestion.description;
      this.address = suggestion.description;
      this.predictions = [];
      this.locationConfirmed = true;
    },
    confirmSearchedLocation() {
      if (!this.searchQuery.trim()) {
        if (this.$toast) this.$toast.error('Please search and select a location.');
        return;
      }

      this.address = this.searchQuery.trim();
      this.closeLocationModal();
      this.applySelectedLocation(this.address);
    },
    confirmPinLocation() {
      const selectedAddress = (this.address || this.searchQuery || '').trim();

      if (selectedAddress && this.locationConfirmed) {
        this.closeLocationModal();
        this.applySelectedLocation(selectedAddress);
        if (this.$toast) this.$toast.success('Location selected!');
        return;
      }

      if (this.lat && this.lng && selectedAddress) {
        this.closeLocationModal();
        this.applySelectedLocation(selectedAddress);
        if (this.$toast) this.$toast.success('Location selected!');
      } else if (this.$toast) {
        this.$toast.error('Please select a location on the map.');
      }
    },
    async handleConfirmation(value) {
      const answer = value.toLowerCase();

      if (['no', 'n', 'cancel', 'restart'].includes(answer)) {
        this.resetBot();
        this.addBotMessage(this.currentContent.greeting);
        this.askPetType();
        return;
      }

      if (!['yes', 'y', 'confirm', 'ok', 'okay'].includes(answer)) {
        this.addBotMessage('Please reply YES to submit or NO to start again.');
        this.setQuickOptions([
          { label: 'YES', value: 'yes' },
          { label: 'NO', value: 'no' },
        ]);
        return;
      }

      await this.submitBooking();
    },
    async submitBooking() {
      const service = this.botPayload.service;
      if (!service) return;

      this.isSubmitting = true;

      try {
        const payload = {
          website_name: this.$websiteName,
          name: this.botPayload.name,
          email: '',
          phoneNumber: this.botPayload.phoneNumber,
          address: this.botPayload.address,
          pin_location: this.botPayload.pin_location || this.buildPinLocationUrl(this.botPayload.address || ''),
          date: this.botPayload.date,
          duration_from: this.botPayload.duration_from,
          duration_to: this.botPayload.duration_to,
          service_ids: [service.id],
          plan_detail_prices_ids: [service.id],
          item_counts: [{ id: service.id, count: 1 }],
          totalPrice: Number(service.price || 0),
        };

        const response = await this.$axios.post('/save-booking', payload);

        if (response.data.success) {
          this.bookingReceiptId = response.data.booking_id;
          this.botState = 'completed';
          this.addBotMessage(
            `Thank you! Your booking request #${response.data.booking_id} has been received.\n\nOur team will confirm your appointment soon. You can download your receipt below.`
          );
        } else {
          this.addBotMessage('Sorry, booking submission failed. Please try again or contact us directly.');
          this.botState = 'awaiting_confirmation';
        }
      } catch (error) {
        console.error('Booking bot submit failed:', error);
        this.addBotMessage('Sorry, something went wrong while submitting your booking. Please try again.');
        this.botState = 'awaiting_confirmation';
      } finally {
        this.isSubmitting = false;
      }
    },
    downloadBookingPDF() {
      if (!this.bookingReceiptId || this.isDownloadingReceipt) return;

      this.isDownloadingReceipt = true;

      try {
        const downloadUrl = getBookingDownloadUrl(this.bookingReceiptId);
        const form = document.createElement('form');
        form.method = 'GET';
        form.action = downloadUrl;
        form.target = '_blank';
        form.rel = 'noopener noreferrer';
        document.body.appendChild(form);
        form.submit();
        document.body.removeChild(form);

        if (this.$toast) this.$toast.success('Booking receipt download started!');
      } catch (error) {
        console.error('Error downloading booking receipt:', error);
        if (this.$toast) this.$toast.error('Failed to download booking receipt.');
      } finally {
        this.isDownloadingReceipt = false;
      }
    },
    parseDate(value) {
      const text = value.trim().toLowerCase();
      const today = new Date();

      if (text === 'today') {
        return today.toISOString().split('T')[0];
      }

      if (text === 'tomorrow') {
        const tomorrow = new Date(today);
        tomorrow.setDate(today.getDate() + 1);
        return tomorrow.toISOString().split('T')[0];
      }

      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) return null;

      return parsed.toISOString().split('T')[0];
    },
  },
};
</script>

<style scoped>
.whatsapp-chat-widget {
  font-family: Avenir, Helvetica, Arial, sans-serif;
}

.whatsapp-chat-widget__actions {
  position: fixed;
  right: 22px;
  bottom: 28px;
  z-index: 1040;
  display: flex;
  align-items: flex-end;
  gap: 18px;
}

.whatsapp-chat-widget__teaser {
  position: relative;
  display: flex;
  align-items: flex-end;
  gap: 0;
  padding: 0;
  color: #ffffff;
  background: transparent;
  border: 0;
  cursor: pointer;
  filter: drop-shadow(0 3px 10px rgba(0, 0, 0, 0.2));
}

.whatsapp-chat-widget__icon {
  position: relative;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 48px;
  width: 48px;
  height: 48px;
  background: #1a1a1a;
  border: 0;
  border-radius: 50%;
  box-shadow: none;
}

.whatsapp-chat-widget__icon--bot {
  flex-shrink: 0;
  background: #1a1a1a;
}

.whatsapp-chat-widget__icon--bot::after {
  position: absolute;
  bottom: -1px;
  left: 6px;
  width: 9px;
  height: 9px;
  background: #1a1a1a;
  border-bottom-left-radius: 2px;
  clip-path: polygon(0 100%, 100% 0, 0 0);
  content: "";
}

.whatsapp-chat-widget__bubbles {
  position: relative;
  display: inline-block;
  width: 26px;
  height: 22px;
}

.whatsapp-chat-widget__bubble-back {
  position: absolute;
  top: 0;
  left: 0;
  color: #8f8f8f;
  font-size: 14px;
}

.whatsapp-chat-widget__bubble-front {
  position: absolute;
  right: 0;
  bottom: 0;
  color: #ffffff;
  font-size: 18px;
}

.whatsapp-chat-widget__whatsapp-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 50px;
  width: 50px;
  height: 50px;
  color: #ffffff;
  font-size: 29px;
  background: #00870e;
  border: 0;
  border-radius: 50%;
  box-shadow: 0 4px 12px rgba(0, 135, 14, 0.28);
  cursor: pointer;
}

.whatsapp-chat-widget__message {
  position: relative;
  z-index: 1;
  display: inline-flex;
  flex-direction: column;
  justify-content: center;
  height: 32px;
  padding: 0 10px 0 12px;
  color: #ffffff;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.1;
  text-align: left;
  background: #11487b;
  border-radius: 14px 0 0 14px;
  box-shadow: none;
}

.whatsapp-chat-widget__message-line {
  display: block;
  white-space: nowrap;
}

.whatsapp-chat-widget__message.is-blinking {
  animation: bot-message-notification 1.8s ease-in-out infinite;
}

@keyframes bot-message-notification {
  0%,
  100% {
    opacity: 1;
    background: #11487b;
    box-shadow: 0 0 0 0 rgba(17, 72, 123, 0.45);
  }

  50% {
    opacity: 0.72;
    background: #1a5f9e;
    box-shadow: 0 0 0 8px rgba(17, 72, 123, 0);
  }
}

@keyframes whatsapp-chat-dot-ping {
  0% {
    opacity: 0.75;
    transform: scale(0.6);
  }

  100% {
    opacity: 0;
    transform: scale(1.45);
  }
}

.whatsapp-chat-widget__backdrop {
  position: fixed;
  inset: 0;
  z-index: 1045;
  background: rgba(0, 0, 0, 0.35);
}

.whatsapp-chat-widget__modal {
  position: fixed;
  right: 22px;
  bottom: 95px;
  z-index: 1050;
  display: flex;
  flex-direction: column;
  width: min(360px, calc(100vw - 32px));
  height: min(560px, calc(100vh - 120px));
  max-height: calc(100vh - 120px);
  overflow: hidden;
  background: #ffffff;
  border-radius: 18px;
  box-shadow: 0 18px 45px rgba(0, 0, 0, 0.25);
}

.whatsapp-chat-widget__header {
  display: flex;
  flex-shrink: 0;
  align-items: flex-start;
  justify-content: space-between;
  padding: 18px;
  color: #ffffff;
  background: linear-gradient(135deg, #075e54, #128c7e);
}

.whatsapp-chat-widget__header h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
}

.whatsapp-chat-widget__header p {
  margin: 4px 0 0;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.85);
}

.whatsapp-chat-widget__status {
  display: inline-block;
  width: 8px;
  height: 8px;
  margin-right: 6px;
  background: #25d366;
  border-radius: 50%;
}

.whatsapp-chat-widget__close {
  flex-shrink: 0;
  color: #ffffff;
  font-size: 28px;
  line-height: 1;
  background: transparent;
  border: 0;
  cursor: pointer;
}

.whatsapp-chat-widget__body {
  flex: 1 1 0;
  min-height: 0;
  padding: 18px;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  background: #efe7dd;
}

.whatsapp-chat-widget__thread {
  min-height: min-content;
}

.whatsapp-chat-widget__row {
  display: flex;
  margin-bottom: 10px;
}

.whatsapp-chat-widget__row.is-bot {
  justify-content: flex-start;
}

.whatsapp-chat-widget__row.is-user {
  justify-content: flex-end;
}

.whatsapp-chat-widget__bubble {
  max-width: 85%;
  padding: 10px 12px;
  color: #1f2933;
  font-size: 14px;
  line-height: 1.45;
  white-space: pre-line;
  background: #ffffff;
  border-radius: 12px;
}

.whatsapp-chat-widget__row.is-user .whatsapp-chat-widget__bubble {
  color: #ffffff;
  background: #128c7e;
}

.whatsapp-chat-widget__quick-replies {
  display: flex;
  flex-shrink: 0;
  flex-wrap: wrap;
  gap: 8px;
  max-height: 140px;
  padding: 0 18px 12px;
  overflow-y: auto;
  background: #efe7dd;
}

.whatsapp-chat-widget__quick-replies button {
  padding: 7px 10px;
  color: #075e54;
  font-size: 12px;
  font-weight: 600;
  background: #ffffff;
  border: 1px solid rgba(7, 94, 84, 0.25);
  border-radius: 999px;
  cursor: pointer;
}

.whatsapp-chat-widget__footer {
  display: flex;
  flex-shrink: 0;
  gap: 8px;
  padding: 14px 18px 18px;
  background: #ffffff;
}

.whatsapp-chat-widget__footer--location {
  justify-content: center;
}

.whatsapp-chat-widget__footer--phone {
  align-items: center;
}

.whatsapp-chat-widget__phone-input {
  display: flex;
  flex: 1;
  align-items: center;
  min-width: 0;
  overflow: hidden;
  background: #ffffff;
  border: 1px solid #d0d5dd;
  border-radius: 999px;
}

.whatsapp-chat-widget__phone-prefix {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  gap: 6px;
  padding: 10px 10px 10px 12px;
  color: #1f2933;
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
  border-right: 1px solid #e5e7eb;
}

.whatsapp-chat-widget__phone-flag {
  width: 18px;
  height: 12px;
  object-fit: cover;
  border-radius: 2px;
}

.whatsapp-chat-widget__input--phone {
  flex: 1;
  min-width: 0;
  border: 0;
  border-radius: 0;
  box-shadow: none;
}

.whatsapp-chat-widget__input--phone:focus {
  box-shadow: none;
}

.whatsapp-chat-widget__calendar-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.whatsapp-chat-widget__calendar-nav-btn {
  width: 32px;
  height: 32px;
  color: #075e54;
  font-size: 22px;
  line-height: 1;
  background: #ffffff;
  border: 1px solid #d0d5dd;
  border-radius: 50%;
  cursor: pointer;
}

.whatsapp-chat-widget__calendar-nav-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.whatsapp-chat-widget__calendar-month {
  color: #1f2933;
  font-size: 15px;
  font-weight: 700;
}

.whatsapp-chat-widget__calendar-weekdays,
.whatsapp-chat-widget__calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 6px;
}

.whatsapp-chat-widget__calendar-weekdays {
  margin-bottom: 8px;
}

.whatsapp-chat-widget__calendar-weekdays span {
  color: #6b7280;
  font-size: 11px;
  font-weight: 700;
  text-align: center;
}

.whatsapp-chat-widget__calendar-day {
  height: 36px;
  color: #1f2933;
  font-size: 13px;
  font-weight: 600;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
}

.whatsapp-chat-widget__calendar-day.is-empty {
  visibility: hidden;
  pointer-events: none;
}

.whatsapp-chat-widget__calendar-day.is-disabled {
  color: #9ca3af;
  background: #f3f4f6;
  cursor: not-allowed;
}

.whatsapp-chat-widget__calendar-day.is-today {
  border-color: #25d366;
}

.whatsapp-chat-widget__calendar-day.is-selected {
  color: #ffffff;
  background: #128c7e;
  border-color: #128c7e;
}

.whatsapp-chat-widget__pick-location,
.whatsapp-chat-widget__download-receipt {
  width: 100%;
  padding: 12px 16px;
  color: #ffffff;
  font-size: 14px;
  font-weight: 700;
  background: #25d366;
  border: 0;
  border-radius: 999px;
  cursor: pointer;
}

.whatsapp-chat-widget__pick-location:disabled,
.whatsapp-chat-widget__download-receipt:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

#BotLocationPickerModal,
#BotDatePickerModal {
  z-index: 1065;
}

.whatsapp-chat-widget__input {
  flex: 1;
  padding: 10px 12px;
  color: #1f2933;
  font-size: 14px;
  background: #ffffff;
  border: 1px solid #d0d5dd;
  border-radius: 999px;
  outline: none;
}

.whatsapp-chat-widget__input:focus {
  border-color: #25d366;
  box-shadow: 0 0 0 3px rgba(37, 211, 102, 0.18);
}

.whatsapp-chat-widget__send {
  padding: 10px 16px;
  color: #ffffff;
  font-size: 14px;
  font-weight: 700;
  background: #25d366;
  border: 0;
  border-radius: 999px;
  cursor: pointer;
}

.whatsapp-chat-widget__send:disabled,
.whatsapp-chat-widget__input:disabled,
.whatsapp-chat-widget__quick-replies button:disabled,
.whatsapp-chat-widget__pick-location:disabled,
.whatsapp-chat-widget__download-receipt:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (max-width: 575px) {
  .whatsapp-chat-widget__actions {
    right: 14px;
    bottom: 82px;
    gap: 12px;
  }

  .whatsapp-chat-widget__message {
    height: 22px;
    padding: 0 8px 0 10px;
    font-size: 10px;
    border-radius: 11px 0 0 11px;
  }

  .whatsapp-chat-widget__icon {
    flex: 0 0 44px;
    width: 44px;
    height: 44px;
  }

  .whatsapp-chat-widget__modal {
    right: 14px;
    bottom: 145px;
    width: calc(100vw - 28px);
    height: min(520px, calc(100vh - 170px));
    max-height: calc(100vh - 170px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .whatsapp-chat-widget__message.is-blinking {
    animation: none;
  }
}
</style>
